/* eslint-disable implicit-arrow-linebreak,max-len */
import { SagaIterator } from "redux-saga";
import {
  all, delay, put, race, select, take, takeEvery, takeLatest,
} from "redux-saga/effects";
import _ from "lodash";

import * as timesheetModelNzActionTypes from "../actions/actionTypes/timesheetModelNzActionTypes";
import * as timesheetModelNzActions from "../actions/timesheetModelNzActions";
import * as timesheetNzActionTypes from "../actions/actionTypes/timesheetNzActionTypes";
import * as timesheetNzActions from "../actions/timesheetNzActions";
import {
  checkTimesheetsAreValid, getApprovalTimesheetIds, getApprovalTimesheets, getLoadingIds, getTimesheetsForApproval,
} from "../transformers/timesheetsApprovalTransformer";
import track from "../../../../logging/logging";

export function* generateTimesheetModel() {
  try {
    const data = yield select((state) => state.timesheetNzReducer);
    const weekStartsOn = yield select((state) => state.businessReducer.timesheetPref.WeekStartsOn);
    yield put({ type: timesheetModelNzActionTypes.UPDATE_TIMESHEET_MODEL_NZ, data: { ...data, weekStartsOn } });
    yield delay(1000);
    yield put(timesheetNzActions.setLoading(false));
  } catch (e) {
    console.error(e);
    yield put({ type: timesheetModelNzActionTypes.UPDATE_TIMESHEET_MODEL_NZ });
  }
}

export const selectTimesheetPrefDay = (state) => ({
  weekStartsOn: state.businessReducer.timesheetPref.WeekStartsOn,
});

export const selectGetTimesheetData = (state) => ({
  locations: state.timesheetNzReducer.locations,
  businessId: state.businessReducer.businessId,
});

export function* fetchTimesheetPrerequisites(data) {
  try {
    yield put(timesheetNzActions.setLoading(true));
    const { weekStartsOn } = yield select(selectTimesheetPrefDay);

    // @ts-ignore
    yield put(timesheetNzActions.getLocations(data.payload));
    yield take(timesheetNzActionTypes.GET_LOCATION_LIST_NZ_SUCCESS);
    const locations = yield select((state) => state.timesheetNzReducer.locations);

    yield all(locations.map((l) =>
    // @ts-ignore
      put(timesheetNzActions.getEmployees({ ...data.payload, locationId: l.locationId }))));

    yield all(locations.map((l) =>
    // @ts-ignore
      put(timesheetNzActions.getTimesheets({ ...data.payload, locationId: l.locationId, weekStartsOn }))));

    yield delay(1000);
    yield put(timesheetNzActions.setLoading(false));
  } catch (e) {
    console.error(e);
    yield put({ type: timesheetModelNzActionTypes.UPDATE_TIMESHEET_MODEL_NZ });
    yield put(timesheetNzActions.setLoading(false));
  }
}

export const selectApprovalTimesheetData = (state) => ({
  timesheets: state.timesheetNzModelReducer.timesheetModel,
  businessId: state.businessReducer.businessId,
});

export function* createTimesheetApprovalRequests(
  action: any,
): SagaIterator {
  try {
    const { timesheets, businessId } = yield select(selectApprovalTimesheetData);
    const approvalTimesheets = getApprovalTimesheets(timesheets, action.payload.userId);
    const loadingIds = getLoadingIds(approvalTimesheets);

    yield put(timesheetModelNzActions.updateLoading(loadingIds, true));

    _.forEach(approvalTimesheets, (t) => {
      track("Daily Timesheet", "Approve", {
        timeCaptureType: t.location.timeCaptureType,
        approvalCategory: action.payload.approvalCategory,
      });
    });

    const { anyZeroHours, approvalPayload } = getTimesheetsForApproval(
      approvalTimesheets,
      action.payload.approvalCategory,
    );

    // check if all the timesheets are valid
    const { allValid, errors } = checkTimesheetsAreValid(approvalPayload, action.payload.approvalCategory);

    if (anyZeroHours) {
      // Display modal to confirm
      yield put(timesheetNzActions.toggleApproveZeroHoursModal(true));

      // Wait for modal response
      const { confirmApproval, cancel } = yield race({
        confirmApproval: take(timesheetNzActionTypes.APPROVE_ZERO_HOURS_CONFIRMATION_NZ),
        cancel: take(timesheetNzActionTypes.APPROVE_ZERO_HOURS_CANCEL_NZ),
      });

      // Stop loading and close modal.
      if (cancel) {
        yield put(timesheetNzActions.toggleApproveZeroHoursModal(false));
        yield put(timesheetModelNzActions.updateLoading(loadingIds, false));
        return;
      }

      yield put(timesheetNzActions.toggleApproveZeroHoursModal(false));
    }

    // if not create some errors
    if (allValid) {
      const requests = _.map(approvalPayload, (user) => put(
        timesheetNzActions.approveEmployeeTimesheets({
          timesheets: user,
          userId: user.userId,
          businessId,
          timesheetIds: getApprovalTimesheetIds(user.timesheets),
          singleEmployee: action.payload.userId !== "",
        }) as any,
      ));

      if (approvalPayload) {
        yield all(requests);
      }
    } else {
      yield put(timesheetNzActions.createInvalidApprovalErrors({ errors }));
      yield put(timesheetModelNzActions.updateLoading(loadingIds, false));
    }
  } catch (error) {
    console.log("error", error);
  }
}

export const selectBusinesses = (state) => ({
  businesses: state.businessReducer.businesses,
  businessId: state.businessReducer.businessId,
});

export function* handleTimesheetApprovalResponse(
  action: any,
): SagaIterator {
  try {
    const { payload, meta } = action;
    const { businesses, businessId } = yield select(selectBusinesses);

    yield put(timesheetModelNzActions.updateStatus({
      userId: meta.userId,
      timesheetIds: meta.timesheetIds,
      status: payload.status,
    }));

    yield put(
      timesheetNzActions.createEmployeeTimesheetAlert({
        businesses,
        businessId,
        row: !meta.singleEmployee,
        userId: meta.userId,
        timesheetIds: meta.timesheetIds,
        status: payload.status,
        detail: payload?.error?.detail?.detail,
        reason: payload?.error?.messageArgs,
      }),
    );
  } catch (error) {
    console.log("error", error);
  }
}

export function* watchActionsToRegenerateTimesheetModel() {
  yield takeLatest(timesheetNzActionTypes.GET_NEW_TIMESHEET_NZ_SUCCESS, generateTimesheetModel);
  yield takeLatest(timesheetNzActionTypes.SET_LOCATION_FILTER_NZ, generateTimesheetModel);
  yield takeLatest(timesheetNzActionTypes.SET_WEEK_FILTER_NZ, generateTimesheetModel);
  yield takeLatest(timesheetNzActionTypes.SET_EMPLOYEE_FILTER_NZ, generateTimesheetModel);
  yield takeLatest(timesheetNzActionTypes.SET_MANAGER_FILTER_NZ, generateTimesheetModel);
  yield takeLatest(timesheetNzActionTypes.RESET_FILTERS_NZ, generateTimesheetModel);
}

export function* watchFetchTimesheetRequest() {
  yield takeLatest(timesheetNzActionTypes.GET_TIMESHEET_PREREQUISITES_NZ, fetchTimesheetPrerequisites);
}

export function* watchApproveTimesheetsRequest(): SagaIterator {
  yield takeEvery(timesheetNzActionTypes.APPROVE_TIMESHEETS_NZ, createTimesheetApprovalRequests);
}

export function* watchApproveTimesheetsResponse(): SagaIterator {
  yield takeEvery(timesheetNzActionTypes.APPROVE_EMPLOYEE_TIMESHEETS_NZ_SUCCESS, handleTimesheetApprovalResponse);
  yield takeEvery(timesheetNzActionTypes.APPROVE_EMPLOYEE_TIMESHEETS_NZ_FAILURE, handleTimesheetApprovalResponse);
}
